/**
 * Canonical public site URL helpers for auth redirects and Stripe return URLs.
 * Never falls back to Rocket/staging domains.
 */

function trimTrailingSlash(url: string): string {
  return url.replace(/\/+$/, '');
}

function isLocalDevHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1';
}

/**
 * Validate and normalize an absolute http(s) URL string.
 * Returns null when missing or invalid.
 */
export function normalizeSiteUrl(raw: string | null | undefined): string | null {
  if (!raw?.trim()) return null;
  try {
    const parsed = new URL(raw.trim());
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
    // Prefer origin only — SITE_URL should not carry a path for Stripe/auth redirects.
    return trimTrailingSlash(parsed.origin);
  } catch {
    return null;
  }
}

/**
 * Server-side canonical site URL from NEXT_PUBLIC_SITE_URL.
 * Throws when missing/invalid (production Stripe paths must fail closed).
 */
export function requireCanonicalSiteUrl(): string {
  const normalized = normalizeSiteUrl(process.env.NEXT_PUBLIC_SITE_URL);
  if (!normalized) {
    throw new Error('SITE_URL_NOT_CONFIGURED');
  }
  return normalized;
}

/**
 * Browser-safe site origin for auth redirects.
 * Prefers validated NEXT_PUBLIC_SITE_URL; allows window.location.origin only in local development.
 */
export function getAuthRedirectOrigin(): string {
  const fromEnv = normalizeSiteUrl(process.env.NEXT_PUBLIC_SITE_URL);
  if (fromEnv) return fromEnv;

  if (typeof window !== 'undefined') {
    try {
      const { hostname, origin } = window.location;
      if (isLocalDevHostname(hostname)) {
        return trimTrailingSlash(origin);
      }
    } catch {
      // fall through
    }
  }

  throw new Error('SITE_URL_NOT_CONFIGURED');
}

export function getEmailConfirmationRedirectUrl(): string {
  return `${getAuthRedirectOrigin()}/auth/callback`;
}

export function getPasswordResetRedirectUrl(): string {
  return `${getAuthRedirectOrigin()}/auth/callback?next=/account-settings`;
}

/** Safe absolute URL join (base without trailing slash + path starting with /). */
export function joinSiteUrl(base: string, path: string): string {
  const root = trimTrailingSlash(base);
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${root}${suffix}`;
}
